import { useEffect, useRef, useState } from "react";
import moment from "moment";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import { useHub, useWallet } from "../../../../core/Global";
import { FontAwesomeIcons } from "../../../../core/Types";
import WalletTransaction from "../../../../models/WalletTransaction";
import { WalletDateRange } from "./WalletDateFilter";


type UsageBreakdown = { sms: number; calls: number; lines: number; other: number }

type Overview = {
    balance: number
    usageTotal: number
    usageBreakdown: UsageBreakdown
    depositsTotal: number
    recentCharges: WalletTransaction[]
}

const money = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
})


// Rounded badge with a duotone icon, used as each card's corner accent.
function CardIcon({ icon, color }: { icon: FontAwesomeIcons; color: string }) {
    return (
        <div className="symbol symbol-45px">
            <span className={`symbol-label bg-light-${color}`}>
                <Icon icon={icon} doutone size={22} className={`text-${color}`} />
            </span>
        </div>
    )
}


// Small donut showing how the period's usage splits across SMS / calls / line rentals / other.
function UsageDonut({ breakdown }: { breakdown: UsageBreakdown }) {

    const ref = useRef<HTMLDivElement>(null)
    const chartRef = useRef<any>(null)

    useEffect(() => {
        if (!ref.current) return
        const Apex = (window as any).ApexCharts
        const KTUtil = (window as any).KTUtil
        if (!Apex || !KTUtil) return

        const options = {
            series: [breakdown.sms, breakdown.calls, breakdown.lines, breakdown.other],
            chart: { type: "donut", width: 92, height: 92, sparkline: { enabled: true } },
            labels: ["SMS", "Calls", "Lines", "Other"],
            colors: [
                KTUtil.getCssVariableValue("--bs-primary"),
                KTUtil.getCssVariableValue("--bs-info"),
                KTUtil.getCssVariableValue("--bs-warning"),
                KTUtil.getCssVariableValue("--bs-gray-300")
            ],
            legend: { show: false },
            dataLabels: { enabled: false },
            plotOptions: { pie: { donut: { size: "62%" } } },
            stroke: { width: 0 },
            tooltip: { y: { formatter: (v: number) => money.format(v) } }
        }

        if (chartRef.current) { try { chartRef.current.destroy() } catch { } }
        chartRef.current = new Apex(ref.current, options)
        chartRef.current.render()

        return () => {
            if (chartRef.current) { try { chartRef.current.destroy() } catch { }; chartRef.current = null }
        }
    }, [breakdown])

    return <div ref={ref} style={{ width: 92, height: 92 }} />
}


// One usage line: colored dot, label, then its amount a short gap away (amounts stay aligned via
// the fixed label width).
function BreakdownRow({ color, label, value }: { color: string; label: string; value?: number }) {
    return (
        <div className="d-flex align-items-center fs-7">
            <span className="d-inline-block rounded-circle me-2"
                style={{ width: 9, height: 9, backgroundColor: `var(${color})` }} />
            <span className="text-gray-600" style={{ minWidth: 42 }}>{label}</span>
            <span className="text-dark fw-semibold ms-4">{money.format(value ?? 0)}</span>
        </div>
    )
}


type Props = {
    range?: WalletDateRange
    refresh?: number
}

export default function WalletStatsCards({ range, refresh }: Props) {

    const hub = useHub()
    const wallet = useWallet()

    const [data, dataSet] = useState<Overview>()

    useEffect(() => {
        if (!range) return
        hub.Get(`/accounts/profile/wallet/overview?start=${range.start}&end=${range.end}`, res => {
            if (res?.success && res.data) dataSet(res.data)
        })
    }, [range?.start, range?.end, refresh])

    const bd = data?.usageBreakdown

    // Average daily spend over the selected period, and a rough runway at that rate.
    const days = range ? Math.max(1, moment(range.end).diff(moment(range.start), "days") + 1) : 1
    const avgDaily = (data?.usageTotal ?? 0) / days
    const runwayDays = avgDaily > 0 ? Math.floor((wallet?.balance ?? 0) / avgDaily) : null

    return (
        <div className="row g-5 mb-5">

            {/* Card 1 — current balance + latest charges. Always live, never tied to the filter. */}
            <div className="col-sm-6 col-xl-3">
                <div className="card h-100">
                    <div className="card-body">
                        <FlexRow justify="between" className="mb-4">
                            <span className="text-gray-600 fw-semibold fs-6">Current Balance</span>
                            <CardIcon icon="wallet" color="primary" />
                        </FlexRow>

                        <div className="fs-2hx fw-bold text-dark">{money.format(wallet?.balance ?? 0)}</div>
                        <span className="text-gray-400 fs-8">Live balance</span>

                        <div className="separator my-4" />

                        <span className="text-gray-500 fw-semibold fs-8 d-block mb-3">Recent Charges</span>
                        {
                            (data?.recentCharges?.length ?? 0) === 0 ?
                                <div className="text-gray-400 fs-8">No charges yet.</div>
                                :
                                <div className="d-flex flex-column gap-3">
                                    {
                                        data!.recentCharges.slice(0, 3).map(t =>
                                            <FlexRow key={t.id} justify="between">
                                                <FlexRow>
                                                    <Icon icon="circle-check" doutone className="text-success me-2" size={14} />
                                                    <span className="fs-8 fw-semibold text-dark">{t.amountText ?? money.format(t.amount)}</span>
                                                </FlexRow>
                                                <span className="fs-9 text-gray-400">{t.shortDate}</span>
                                            </FlexRow>
                                        )
                                    }
                                </div>
                        }
                    </div>
                </div>
            </div>

            {/* Card 2 — usage in the period, with breakdown + donut. */}
            <div className="col-sm-6 col-xl-3">
                <div className="card h-100">
                    <div className="card-body">
                        <FlexRow justify="between" className="mb-4">
                            <span className="text-gray-600 fw-semibold fs-6">Usage</span>
                            <CardIcon icon="arrow-up-from-bracket" color="danger" />
                        </FlexRow>

                        <div className="fs-2hx fw-bold text-dark">{money.format(data?.usageTotal ?? 0)}</div>
                        <span className="text-gray-400 fs-8">{range?.label ? `In ${range.label}` : "In selected period"}</span>

                        <FlexRow justify="between" alignItems="end" className="mt-4">
                            <div className="d-flex flex-column gap-2">
                                <BreakdownRow color="--bs-primary" label="SMS" value={bd?.sms} />
                                <BreakdownRow color="--bs-info" label="Calls" value={bd?.calls} />
                                <BreakdownRow color="--bs-warning" label="Lines" value={bd?.lines} />
                            </div>
                            {
                                (data?.usageTotal ?? 0) > 0 && bd &&
                                <UsageDonut breakdown={bd} />
                            }
                        </FlexRow>
                    </div>
                </div>
            </div>

            {/* Card 3 — top-ups (deposits) in the period. */}
            <div className="col-sm-6 col-xl-3">
                <div className="card h-100">
                    <div className="card-body">
                        <FlexRow justify="between" className="mb-4">
                            <span className="text-gray-600 fw-semibold fs-6">Top-ups</span>
                            <CardIcon icon="arrow-down-to-bracket" color="success" />
                        </FlexRow>

                        <div className="fs-2hx fw-bold text-success">{money.format(data?.depositsTotal ?? 0)}</div>
                        <span className="text-gray-400 fs-8">{range?.label ? `In ${range.label}` : "In selected period"}</span>
                    </div>
                </div>
            </div>

            {/* Card 4 — average daily spend + a rough runway at that rate (suggested). */}
            <div className="col-sm-6 col-xl-3">
                <div className="card h-100">
                    <div className="card-body">
                        <FlexRow justify="between" className="mb-4">
                            <span className="text-gray-600 fw-semibold fs-6">Avg Daily Usage</span>
                            <CardIcon icon="gauge-high" color="info" />
                        </FlexRow>

                        <div className="fs-2hx fw-bold text-dark">{money.format(avgDaily)}</div>
                        <span className="text-gray-400 fs-8">Per day in this period</span>

                        <div className="separator my-4" />

                        <FlexRow justify="between">
                            <span className="text-gray-500 fs-8 fw-semibold">Estimated runway</span>
                            <span className="fs-7 fw-bold text-dark">
                                {runwayDays === null ? "—" : `${runwayDays} day${runwayDays === 1 ? "" : "s"}`}
                            </span>
                        </FlexRow>
                    </div>
                </div>
            </div>

        </div>
    )
}
